/**
 * AI Recruit - Resumes Page
 * Resume management and AI analysis dashboard with agentic AI
 */

import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/hooks/useAuth'
import { usePermission } from '@/hooks/usePermission'
import { formatToCompactDateTime, formatDurationBetween } from '../../utils/dateUtils'
import ConfirmationModal from '../../components/common/ConfirmationModal'
import UploadHistoryPanel from '../../components/resume/UploadHistoryPanel'

const formatPhoneNumber = (phone: string): string => {
  if (!phone) return ''
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '')
  
  // Format based on length and country code pattern
  if (digits.length === 10) {
    // 10 digits - US without country code: (234) 567-8901
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  } 
  else if (digits.length === 11 && digits.startsWith('1')) {
    // 11 digits starting with 1 - US with country code: +1 (234) 567-8901
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  } 
  else if (digits.length === 12 && digits.startsWith('91')) {
    // 12 digits starting with 91 - Indian with country code: +91 12345 67890
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`
  }
  else if (digits.length === 11 && digits.startsWith('91')) {
    // 11 digits starting with 91
    return `+91 ${digits.slice(2)}`
  }
  else if (digits.length > 10) {
    // Generic international format
    if (digits.startsWith('91')) {
      return `+91 ${digits.slice(2)}`
    } else if (digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
    } else {
      return `+${digits.slice(0, 2)} ${digits.slice(2)}`
    }
  } 
  else {
    return phone
  }
}

import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  SparklesIcon,
  LightBulbIcon,
  ChartBarIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { CheckCircleIcon, ClockIcon, ExclamationCircleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/solid'

import { resumesApi, resumeGenerationApi } from '@/services/api'
import { fetchSources } from '@/services/sourceApi'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import UploadModal from '@/components/resume/UploadModal'
import ResumeInfoTooltip from '@/components/resume/ResumeInfoTooltip'
import ResumeRowActionsMenu from '@/components/resume/ResumeRowActionsMenu'
import { useBulkUpload } from '@/contexts/BulkUploadContext'
import { downloadResumeAsWord } from '@/utils/downloadResumeAsWord'

interface Resume {
  id: string
  candidate_id: string
  filename: string
  candidate_name?: string
  candidate_email?: string
  professional_title?: string
  source_id?: string
  source_name?: string
  source_is_active?: boolean
  analysis?: {
    personal_info?: {
      professional_title?: string
    }
  }
  status: 'uploaded' | 'processing' | 'analyzed' | 'failed'
  quality_score?: number | null
  years_experience?: number | null
  upload_date: string
  processing_started?: string | null
  processing_completed?: string | null
  uploaded_by_name?: string
  // Duplicate detection fields
  is_duplicate?: boolean
  duplicate_confidence?: number
  duplicate_matches?: Array<{
    resume_id: string
    candidate_name?: string
    match_type: string
    confidence: number
    filename: string
  }>
  duplicate_action?: string
  duplicate_reasoning?: string
}

// A resume can get stuck at 'processing' (worker crash, dropped task, etc.)
// with no automatic way out. Past this many minutes since processing_started,
// treat it as stale rather than actively running, so Reprocess stays available.
const STALE_PROCESSING_MINUTES = 10

const isStaleProcessing = (resume: Resume): boolean => {
  if (resume.status !== 'processing' || !resume.processing_started) return false
  const elapsedMs = Date.now() - new Date(resume.processing_started).getTime()
  return elapsedMs > STALE_PROCESSING_MINUTES * 60 * 1000
}

const ResumesPage: React.FC = () => {
  // Use URL search params for persistent state
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Get values from URL or use defaults
  const activeSearchTerm = searchParams.get('search') || ''
  const skillFilter = searchParams.get('skill') || ''
  const jobTitleFilter = searchParams.get('job_title') || ''
  const minExpFilter = searchParams.get('min_experience') || ''
  const maxExpFilter = searchParams.get('max_experience') || ''
  const sourceFilter = searchParams.get('source') || ''
  const statusFilter = searchParams.get('status') || ''
  const sortBy = searchParams.get('sort_by') || 'created_at'
  const sortOrder = (searchParams.get('sort_order') || 'desc') as 'asc' | 'desc'
  const currentPage = parseInt(searchParams.get('page') || '1')
  const pageSize = parseInt(searchParams.get('page_size') || '10')

  // Local state for search inputs (before submission)
  const [searchInput, setSearchInput] = useState(activeSearchTerm)
  const [skillInput, setSkillInput] = useState(skillFilter)
  const [jobTitleInput, setJobTitleInput] = useState(jobTitleFilter)
  const [minExpInput, setMinExpInput] = useState(minExpFilter)
  const [maxExpInput, setMaxExpInput] = useState(maxExpFilter)

  // Sync inputs with URL params when component mounts or URL changes
  useEffect(() => {
    setSearchInput(activeSearchTerm)
  }, [activeSearchTerm])
  useEffect(() => {
    setSkillInput(skillFilter)
  }, [skillFilter])
  useEffect(() => {
    setJobTitleInput(jobTitleFilter)
  }, [jobTitleFilter])
  useEffect(() => {
    setMinExpInput(minExpFilter)
  }, [minExpFilter])
  useEffect(() => {
    setMaxExpInput(maxExpFilter)
  }, [maxExpFilter])
  
  // const [useSemanticSearch, setUseSemanticSearch] = useState(false)
  const useSemanticSearch = false // AI search functionality disabled
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  // Bulk upload is hosted app-wide by BulkUploadProvider, so a running batch
  // keeps going (and stays visible) if the recruiter navigates away.
  const { openBulkUpload } = useBulkUpload()
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([])
  const [alternativeQueries, setAlternativeQueries] = useState<string[]>([])
  const [searchIntent, setSearchIntent] = useState<any>(null)
  const [showLoadingPopup, setShowLoadingPopup] = useState(false)

  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [resumeIdToDelete, setresumeIdToDelete] = useState<string | null>(null)
  
  // Check user roles
  const { can } = usePermission()
  const isAdmin = can('resumes.delete')
  const canReprocess = can('resumes.reprocess') // §5.6.E
  // Gate mismatch fix (Priority 4 audit) — the Upload Resumes/Bulk Upload
  // buttons rendered unconditionally even though the backend has always
  // gated resume upload at recruiter-tier (gated("resumes.upload", ...),
  // resume_upload_and_match.py). Frontend-only change.
  const canUploadResumes = can('resumes.upload')
  // Added 2026-08-25 (permission-catalog round 2, §26) — the list and detail
  // views, plus the download-original/download-formatted actions, had no
  // gate of any kind (frontend or backend) until now. Both new keys default
  // to all roles, matching today's actual unrestricted behavior exactly.
  // Split 2026-08-26 from a single resumes.download into two independently
  // grantable keys, one per button below — both still default to all
  // roles (min_role stayed "viewer" for each), so nothing changes for
  // anyone's actual access.
  const canDownloadOriginal = can('resumes.downloadOriginal')
  const canDownloadFormatted = can('resumes.downloadFormatted')

  // Handle search mode switch - Basic Search only
  const handleSearchModeSwitch = useCallback((newMode: boolean) => {
    // Only allow basic search mode (newMode = false)
    if (!newMode) {
      // Clear search state when switching modes
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('search')
      newParams.set('page', '1')
      setSearchParams(newParams)
      setSearchInput('')
      setSearchSuggestions([])
      setAlternativeQueries([])
      setSearchIntent(null)
    }
  }, [searchParams, setSearchParams])

  // Handle search execution
  const handleSearch = useCallback(async () => {
    if (!searchInput.trim() && useSemanticSearch) {
      toast.error('Please enter a search query for AI search')
      return
    }
    
    // Update URL params — apply all independent search criteria together.
    const newParams = new URLSearchParams(searchParams)
    const setOrDelete = (key: string, value: string) => {
      if (value.trim()) {
        newParams.set(key, value.trim())
      } else {
        newParams.delete(key)
      }
    }
    setOrDelete('search', searchInput)
    setOrDelete('skill', skillInput)
    setOrDelete('job_title', jobTitleInput)
    setOrDelete('min_experience', minExpInput)
    setOrDelete('max_experience', maxExpInput)
    newParams.set('page', '1') // Reset to first page when searching
    setSearchParams(newParams)
  }, [searchInput, skillInput, jobTitleInput, minExpInput, maxExpInput, useSemanticSearch, searchParams, setSearchParams])

  // Clear all search criteria (free-text + skill/title/experience filters)
  const handleClearSearch = useCallback(() => {
    setSearchInput('')
    setSkillInput('')
    setJobTitleInput('')
    setMinExpInput('')
    setMaxExpInput('')
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('search')
    newParams.delete('skill')
    newParams.delete('job_title')
    newParams.delete('min_experience')
    newParams.delete('max_experience')
    newParams.set('page', '1')
    setSearchParams(newParams)
  }, [searchParams, setSearchParams])

  // Clear only filters (not search)
  const handleClearFilters = useCallback(() => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('source')
    newParams.delete('status')
    newParams.set('sort_by', 'created_at')
    newParams.set('sort_order', 'desc')
    newParams.set('page', '1')
    setSearchParams(newParams)
  }, [searchParams, setSearchParams])

  // Clear a single filter param, leaving the others untouched
  const clearFilterParam = (key: 'source' | 'status') => {
    const p = new URLSearchParams(searchParams)
    p.delete(key)
    p.set('page', '1')
    setSearchParams(p)
  }

  // Clear a single search/filter field (search, skill, job title, min/max
  // experience), leaving the other 4 untouched — unlike handleClearSearch,
  // which resets all 5 together.
  const clearSingleField = (key: string, resetInput: () => void) => {
    resetInput()
    const p = new URLSearchParams(searchParams)
    p.delete(key)
    p.set('page', '1')
    setSearchParams(p)
  }

  const clearSort = () => {
    const p = new URLSearchParams(searchParams)
    p.set('sort_by', 'created_at')
    p.set('sort_order', 'desc')
    p.set('page', '1')
    setSearchParams(p)
  }

  const renderFilterChip = (label: string, value: string, onClear: () => void) => (
    <span className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full">
      <span className="font-semibold">{label}:</span> {value}
      <button
        type="button"
        onClick={onClear}
        className="ml-0.5 p-0.5 rounded-full text-blue-500 hover:text-blue-700 hover:bg-blue-100"
        aria-label={`Remove ${label} filter`}
      >
        <XMarkIcon className="w-3 h-3" />
      </button>
    </span>
  )

  const sortByLabel = (value: string) =>
    value === 'candidate_name' ? 'Candidate Name' :
    value === 'processing_completed_at' ? 'Processing Date' :
    value === 'years_experience' ? 'Experience' : 'Upload Date'

  // Handle Enter key press
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }, [handleSearch])

  // Debounced auto-search: after the user stops typing in any of the 5
  // search/filter inputs (search, skill, job title, min/max experience) for
  // 400ms, push all of them into the URL together — the same effect the
  // removed Search button used to trigger on click. Enter (handleKeyPress)
  // still applies everything immediately, same as before.
  useEffect(() => {
    const trimmedSearch = searchInput.trim()
    const trimmedSkill = skillInput.trim()
    const trimmedJobTitle = jobTitleInput.trim()
    const trimmedMinExp = minExpInput.trim()
    const trimmedMaxExp = maxExpInput.trim()

    if (
      trimmedSearch === activeSearchTerm &&
      trimmedSkill === skillFilter &&
      trimmedJobTitle === jobTitleFilter &&
      trimmedMinExp === minExpFilter &&
      trimmedMaxExp === maxExpFilter
    ) {
      return
    }

    const timeoutId = setTimeout(() => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev)
        const setOrDelete = (key: string, value: string) => {
          if (value) {
            p.set(key, value)
          } else {
            p.delete(key)
          }
        }
        setOrDelete('search', trimmedSearch)
        setOrDelete('skill', trimmedSkill)
        setOrDelete('job_title', trimmedJobTitle)
        setOrDelete('min_experience', trimmedMinExp)
        setOrDelete('max_experience', trimmedMaxExp)
        p.set('page', '1')
        return p
      })
    }, 400)

    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, skillInput, jobTitleInput, minExpInput, maxExpInput])

  // Count active filters (only applied filters, not staged)
  // Count sort as active if it's different from default (created_at, desc)
  const isSortActive = sortBy !== 'created_at' || sortOrder !== 'desc'
  const activeFilterCount = [sourceFilter, statusFilter, isSortActive].filter(Boolean).length

  // Fetch resumes with server-side pagination, sorting, and filtering
  const { data: resumesData, isLoading, error } = useQuery({
    queryKey: ['resumes', activeSearchTerm, skillFilter, jobTitleFilter, minExpFilter, maxExpFilter, useSemanticSearch, currentPage, pageSize, sortBy, sortOrder, sourceFilter, statusFilter],
    queryFn: async () => {
      if (useSemanticSearch && activeSearchTerm.trim()) {
        const response = await resumesApi.semanticSearch({
          query: activeSearchTerm,
          max_results: 100,
          include_analysis: true
        })

        console.log('Semantic search response:', response.data)

        // Update search metadata
        setSearchSuggestions(response.data.suggestions || [])
        setAlternativeQueries(response.data.alternative_queries || [])
        setSearchIntent(response.data.search_intent)

        // Transform semantic search results to match regular list format
        const transformedData = {
          data: {
            resumes: response.data.results.map((result: any) => ({
              id: result.id,
              filename: `${result.candidate_name || 'Unknown'}_resume`,
              candidate_name: result.candidate_name,
              candidate_email: result.candidate_email,
              professional_title: result.professional_title,
              status: 'analyzed',
              quality_score: result.quality_score,
              years_experience: result.years_of_experience,
              upload_date: result.upload_date,
              processing_started: result.processing_started,
              processing_completed: result.processing_completed,
              relevance_score: result.relevance_score,
              skill_match_score: result.skill_match_score,
              experience_match_score: result.experience_match_score,
              semantic_similarity: result.semantic_similarity,
              matched_skills: result.matched_skills,
              matched_experience: result.matched_experience,
              ai_reasoning: result.ai_reasoning,
              highlight_snippets: result.highlight_snippets
            })),
            total: response.data.total_matches,
            search_time_ms: response.data.search_time_ms
          }
        }

        console.log('Transformed semantic search data:', transformedData)
        return transformedData
      } else {
        // Server-side pagination, sorting, and filtering
        setSearchSuggestions([])
        setAlternativeQueries([])
        setSearchIntent(null)

        const response = await resumesApi.list({
          search: activeSearchTerm || undefined,
          skill: skillFilter || undefined,
          job_title: jobTitleFilter || undefined,
          min_experience: minExpFilter ? Number(minExpFilter) : undefined,
          max_experience: maxExpFilter ? Number(maxExpFilter) : undefined,
          page: currentPage,
          page_size: pageSize,
          sort_by: sortBy,
          sort_order: sortOrder,
          source_id: sourceFilter || undefined,
          status: statusFilter || undefined,
        })

        console.log('Server-side paginated response:', response.data)
        return response
      }
    },
    refetchInterval: useSemanticSearch ? undefined : (query: any) => {
      const list = query.state.data?.data?.resumes || []
      const hasProcessing = list.some((r: any) => r.status === 'processing' || r.status === 'uploaded')
      return hasProcessing ? 5000 : false
    },
  })

  // Fetch resume sources for filter dropdown
  const { data: resumeSources = [] } = useQuery({
    queryKey: ['resume-sources'],
    queryFn: () => fetchSources('resume', false) // Only active sources
  })

  // Get resumes directly from server response (sorting, filtering, pagination done server-side)
  const resumes = resumesData?.data?.resumes || []

  // Get pagination info from server response
  const totalResumes = resumesData?.data?.total || 0
  const totalPages = Math.ceil(totalResumes / pageSize)
  const hasNext = resumesData?.data?.has_next || currentPage < totalPages

  // Track previous resume statuses to detect processing → analyzed/failed transitions
  const prevStatusesRef = useRef<Record<string, string>>({})

  useEffect(() => {
    const currentResumes: any[] = resumesData?.data?.resumes || []
    const prev = prevStatusesRef.current

    currentResumes.forEach((resume: any) => {
      const prevStatus = prev[resume.id]
      if (prevStatus === 'processing' || prevStatus === 'uploaded') {
        const name = resume.candidate_name || resume.filename || 'Resume'
        if (resume.status === 'analyzed') {
          toast.success(`${name} has been analyzed successfully`)
        } else if (resume.status === 'failed') {
          toast.error(`Analysis failed for ${name}`)
        }
      }
    })

    const next: Record<string, string> = {}
    currentResumes.forEach((resume: any) => { next[resume.id] = resume.status })
    prevStatusesRef.current = next
  }, [resumesData])

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (resumeId: string) => resumesApi.delete(resumeId),
    onSuccess: () => {
      toast.success('Resume deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      setIsDeleteModalOpen(false)
      setresumeIdToDelete(null)
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Delete failed'
      toast.error(message)
      setIsDeleteModalOpen(false)
      setresumeIdToDelete(null)
    }
  })

  const handleOpenDeleteModal = (jobId: string) => {
    setresumeIdToDelete(jobId);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (resumeIdToDelete) {
      deleteMutation.mutate(resumeIdToDelete);
    }
  }

  // Reprocess mutation
  const reprocessMutation = useMutation({
    mutationFn: (resumeId: string) => resumesApi.reprocess(resumeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Reprocess failed'
      toast.error(message)
    }
  })

  const downloadOriginalMutation = useMutation({
    mutationFn: (vars: { id: string; filename?: string }) => resumesApi.download(vars.id), // Uses your existing API function
    onSuccess: (response, vars) => {
      // Prefer the original filename we already have from the list row; fall
      // back to the Content-Disposition header when available (it is only
      // readable when the backend exposes it via CORS).
      let filename = vars.filename || '';

      if (!filename) {
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          // Try different patterns to extract filename
          const patterns = [
            /filename="([^"]+)"/,  // filename="file.pdf"
            /filename=([^;]+)/,    // filename=file.pdf
            /filename="?([^";\s]+)"?/  // filename=file.pdf or filename="file.pdf"
          ];

          for (const pattern of patterns) {
            const match = contentDisposition.match(pattern);
            if (match && match[1]) {
              filename = match[1].trim();
              break;
            }
          }
        }
      }

      // Last-resort fallback: derive an extension from the response content type
      // so we never mislabel a .docx as a .pdf.
      if (!filename) {
        const ct = String(response.headers['content-type'] || '')
        const ext = ct.includes('wordprocessingml') ? 'docx'
          : ct.includes('msword') ? 'doc'
          : ct.includes('pdf') ? 'pdf'
          : ct.includes('text/plain') ? 'txt'
          : 'bin'
        filename = `resume.${ext}`
      }

      console.log('Extracted filename:', filename);

      // Create blob URL and download
      const blob = new Blob([response.data], { type: String(response.headers['content-type'] || 'application/octet-stream') });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Original resume downloaded: ${filename}`);
    },
    onError: (error: any) => {
      toast.error('Failed to download original resume: ' + (error.response?.data?.detail || error.message));
    }
  });


  // Download clean resume mutation
  const downloadMutation = useMutation({
    mutationFn: (vars: { id: string; candidateName: string }) => resumeGenerationApi.generateCleanResume(vars.id),
    onSuccess: (response, vars) => {
      // Dismiss loading toast
      toast.dismiss(`resume-download-${vars.id}`)

      // Check cache status from response headers
      const cacheStatus = response.headers['x-cache-status'] || 'UNKNOWN'
      const isCached = cacheStatus === 'HIT'

      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      const candidateName = vars.candidateName || 'Professional'
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
      link.download = `${candidateName}_Formatted_Resume_${timestamp}.docx`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      // Show appropriate success message based on cache status
      if (isCached) {
        toast.success(`Formatted resume downloaded for ${candidateName}! (Cached)`, {
          duration: 4000,
          icon: '📄'
        })
      } else {
        toast.success(`Formatted resume generated and downloaded for ${candidateName}!`, {
          duration: 4000,
          icon: '📄'
        })
      }
    },
    onError: (error: any, vars: { id: string; candidateName: string }) => {
      // Dismiss loading toast
      toast.dismiss(`resume-download-${vars.id}`)

      const message = error.response?.data?.detail || error.message || 'Failed to generate resume'
      console.error('Resume generation error:', error)
      
      toast.error(`❌ ${message}`, {
        duration: 5000
      })
    }
  })

  // Download clean resume function
  const downloadCleanResume = (resumeId: string, candidateName: string) => {
    toast.loading(`Checking for cached formatted resume for ${candidateName}...`, {
      id: `resume-download-${resumeId}`
    })
    downloadMutation.mutate({ id: resumeId, candidateName })
  }

  // Download resume as Word document function (using LLM formatter)
  const handleDownloadResume = (resume: Resume) => {
    if (resume.status !== 'analyzed') {
      toast.error('Resume must be analyzed before downloading')
      return
    }
    
    // Use the backend LLM-based formatter
    downloadCleanResume(resume.id, resume.candidate_name || 'Resume')
  }



  // Status badge component
  const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const statusConfig = {
      uploaded: { icon: ClockIcon, text: 'Queued', className: 'bg-blue-100 text-blue-800' },
      processing: { icon: ClockIcon, text: 'Processing', className: 'bg-yellow-100 text-yellow-800' },
      analyzed: { icon: CheckCircleIcon, text: 'Analyzed', className: 'bg-green-100 text-green-800' },
      failed: { icon: ExclamationCircleIcon, text: 'Failed', className: 'bg-red-100 text-red-800' },
      // Anything else the backend ever sends that this map doesn't recognize
      // fails safe here — a neutral "Unknown" badge, not an alarming red
      // "Failed" for a status that hasn't actually failed.
      unknown: { icon: QuestionMarkCircleIcon, text: 'Unknown', className: 'bg-gray-100 text-gray-700' },
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.unknown
    const Icon = config.icon
    const isActive = status === 'processing' || status === 'uploaded'

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        <Icon className="w-3 h-3 shrink-0" />
        {config.text}
        {isActive && (
          <svg className="w-3 h-3 animate-spin shrink-0" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        )}
      </span>
    )
  }

  // Resume timing tooltip content — upload/processing timestamps and durations.
  // Long values (full date+timezone strings) get their own line so they never
  // wrap mid-value; short durations stay on one inline row.
  const ResumeTimingTooltip: React.FC<{ resume: Resume }> = ({ resume }) => {
    type Row = { label: string; value: string; stacked?: boolean }
    const rows: Row[] = []

    if (resume.uploaded_by_name) {
      rows.push({ label: 'Uploaded by', value: resume.uploaded_by_name, stacked: true })
    }
    rows.push({ label: 'Uploaded', value: formatToCompactDateTime(resume.upload_date), stacked: true })
    if (resume.processing_started) {
      rows.push({ label: 'Started processing', value: formatToCompactDateTime(resume.processing_started), stacked: true })
      rows.push({ label: 'Queue wait', value: formatDurationBetween(resume.upload_date, resume.processing_started) })
    }
    if (resume.processing_completed) {
      rows.push({ label: 'Last processed', value: formatToCompactDateTime(resume.processing_completed), stacked: true })
    }
    if (resume.processing_started) {
      rows.push({
        label: 'Processing time',
        value: resume.processing_completed
          ? formatDurationBetween(resume.processing_started, resume.processing_completed)
          : `${formatDurationBetween(resume.processing_started)} (ongoing)`,
      })
    }

    return (
      <div className="w-full divide-y divide-gray-100">
        {rows.map((row) =>
          row.stacked ? (
            <div key={row.label} className="py-1.5 first:pt-0 last:pb-0">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{row.label}</div>
              <div className="text-xs font-medium text-gray-900">{row.value}</div>
            </div>
          ) : (
            <div key={row.label} className="flex items-center justify-between py-1.5 first:pt-0 last:pb-0">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{row.label}</span>
              <span className="text-xs font-medium text-gray-900">{row.value}</span>
            </div>
          )
        )}
      </div>
    )
  }

  // Duplicate badge component
  const DuplicateBadge: React.FC<{ resume: Resume }> = ({ resume }) => {
    if (!resume.is_duplicate) return null

    const confidence = resume.duplicate_confidence || 0
    const matchCount = resume.duplicate_matches?.length || 0

    return (
      <div className="flex items-center space-x-1">
        <span 
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            confidence >= 0.8 
              ? 'bg-red-100 text-red-800 border border-red-200' 
              : confidence >= 0.6 
              ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
              : 'bg-orange-100 text-orange-800 border border-orange-200'
          }`}
          title={`${Math.round(confidence * 100)}% confidence duplicate with ${matchCount} match${matchCount !== 1 ? 'es' : ''}`}
        >
          <DocumentDuplicateIcon className="w-3 h-3 mr-1" />
          Duplicate ({Math.round(confidence * 100)}%)
        </span>
        {confidence >= 0.8 && (
          <ExclamationTriangleIcon 
            className="w-4 h-4 text-red-500" 
            title="High confidence duplicate - requires review"
          />
        )}
      </div>
    )
  }


  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold text-gray-900">Candidate Management</h1>
          <ResumeInfoTooltip content="Upload and analyze resumes" panelWidth={200}>
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-gray-200 cursor-pointer transition-colors">
              <InformationCircleIcon className="w-4 h-4 text-gray-400 hover:text-blue-600" />
            </span>
          </ResumeInfoTooltip>
        </div>
        {canUploadResumes && (
          <div className="flex items-center space-x-2">
            <UploadHistoryPanel />
            <button
              onClick={openBulkUpload}
              className="bg-blue-600 text-white px-3 py-1.5 text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1.5"
              title="Upload multiple resumes at once"
            >
              <CloudArrowUpIcon className="w-4 h-4" />
              <span>Upload Resume(s)</span>
            </button>
          </div>
        )}
      </div>

      {/* Search & Filters - one compact card */}
      <div className="bg-white rounded-lg shadow px-4 py-2.5 space-y-2">
        {/* Search / Skill / Job Title / Min Exp / Max Exp - one row */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {/* Search */}
          <div className="flex items-center gap-2">
            <div className="relative w-56">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, mobile, id , skills or title ..."
                className="w-full pl-8 pr-3 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyPress}
              />
            </div>

            {/* Clear Button - Clears the search box only */}
            {(activeSearchTerm || searchInput) && (
              <button
                onClick={() => clearSingleField('search', () => setSearchInput(''))}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Clear search"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Skill - exact match (Scenario 1, 2, 4) */}
          <div className="flex items-center gap-1.5">
            <label className="text-xs font-medium text-gray-700 whitespace-nowrap">Skill:</label>
            <input
              type="text"
              placeholder="e.g. Java, SAP SD"
              title="Exact skill / sub-skill match"
              className="w-28 border border-gray-300 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              onKeyDown={handleKeyPress}
            />
            {(skillFilter || skillInput) && (
              <button
                onClick={() => clearSingleField('skill', () => setSkillInput(''))}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Clear skill"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Job Title */}
          <div className="flex items-center gap-1.5">
            <label className="text-xs font-medium text-gray-700 whitespace-nowrap">Job Title:</label>
            <input
              type="text"
              placeholder="e.g. Senior Software Engineer"
              title="Filter by professional title"
              className="w-40 border border-gray-300 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={jobTitleInput}
              onChange={(e) => setJobTitleInput(e.target.value)}
              onKeyDown={handleKeyPress}
            />
            {(jobTitleFilter || jobTitleInput) && (
              <button
                onClick={() => clearSingleField('job_title', () => setJobTitleInput(''))}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Clear job title"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Min Experience */}
          <div className="flex items-center gap-1.5">
            <label className="text-xs font-medium text-gray-700 whitespace-nowrap">Min Exp:</label>
            <input
              type="number"
              min={0}
              step={0.5}
              placeholder="3"
              title="Minimum years of experience"
              className="w-16 border border-gray-300 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={minExpInput}
              onChange={(e) => setMinExpInput(e.target.value)}
              onKeyDown={handleKeyPress}
            />
            {(minExpFilter || minExpInput) && (
              <button
                onClick={() => clearSingleField('min_experience', () => setMinExpInput(''))}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Clear min experience"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Max Experience */}
          <div className="flex items-center gap-1.5">
            <label className="text-xs font-medium text-gray-700 whitespace-nowrap">Max Exp:</label>
            <input
              type="number"
              min={0}
              step={0.5}
              placeholder="8"
              title="Maximum years of experience"
              className="w-16 border border-gray-300 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={maxExpInput}
              onChange={(e) => setMaxExpInput(e.target.value)}
              onKeyDown={handleKeyPress}
            />
            {(maxExpFilter || maxExpInput) && (
              <button
                onClick={() => clearSingleField('max_experience', () => setMaxExpInput(''))}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Clear max experience"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Source Filter */}
          <div className="flex items-center gap-1.5">
            <label className="text-xs font-medium text-gray-700 whitespace-nowrap">Source:</label>
            <select
              className="border border-gray-300 rounded-lg pl-2 pr-6 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[110px]"
              value={sourceFilter}
              onChange={(e) => {
                const newParams = new URLSearchParams(searchParams)
                if (e.target.value) {
                  newParams.set('source', e.target.value)
                } else {
                  newParams.delete('source')
                }
                newParams.set('page', '1')
                setSearchParams(newParams)
              }}
            >
              <option value="">All Sources</option>
              {resumeSources.map((source: any) => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-1.5">
            <label className="text-xs font-medium text-gray-700 whitespace-nowrap">Status:</label>
            <select
              className="border border-gray-300 rounded-lg pl-2 pr-6 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[110px]"
              value={statusFilter}
              onChange={(e) => {
                const newParams = new URLSearchParams(searchParams)
                if (e.target.value) {
                  newParams.set('status', e.target.value)
                } else {
                  newParams.delete('status')
                }
                newParams.set('page', '1')
                setSearchParams(newParams)
              }}
            >
              <option value="">All Statuses</option>
              <option value="uploaded">Queued</option>
              <option value="processing">Processing</option>
              <option value="analyzed">Analyzed</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {/* Sort Controls */}
          <div className="flex items-center gap-1.5">
            <label className="text-xs font-medium text-gray-700 whitespace-nowrap">Sort by:</label>
            <select
              className="border border-gray-300 rounded-lg pl-2 pr-6 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[110px]"
              value={sortBy}
              onChange={(e) => {
                const newParams = new URLSearchParams(searchParams)
                newParams.set('sort_by', e.target.value)
                newParams.set('page', '1')
                setSearchParams(newParams)
              }}
            >
              <option value="created_at">Upload Date</option>
              <option value="candidate_name">Candidate Name</option>
              <option value="processing_completed_at">Processing Date</option>
              <option value="years_experience">Experience</option>
            </select>

            <button
              onClick={() => {
                const newParams = new URLSearchParams(searchParams)
                const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc'
                newParams.set('sort_order', newSortOrder)
                newParams.set('page', '1')
                setSearchParams(newParams)
              }}
              className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              {sortOrder === 'asc' ? (
                <ChevronUpIcon className="w-4 h-4" />
              ) : (
                <ChevronDownIcon className="w-4 h-4" />
              )}
              <span>{sortOrder === 'asc' ? 'Asc' : 'Desc'}</span>
            </button>
          </div>
        </div>

        {/* Applied criteria chips + clear actions */}
        {(skillFilter || jobTitleFilter || minExpFilter || maxExpFilter || activeFilterCount > 0) && (
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-gray-100">
            <div className="flex flex-wrap items-center gap-2">
              <FunnelIcon className="w-3.5 h-3.5 text-gray-400" />
              {skillFilter && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                  Skill: {skillFilter}
                </span>
              )}
              {jobTitleFilter && (
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                  Title: {jobTitleFilter}
                </span>
              )}
              {minExpFilter && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                  Min Exp: {minExpFilter} yrs
                </span>
              )}
              {maxExpFilter && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                  Max Exp: {maxExpFilter} yrs
                </span>
              )}
              {sourceFilter && renderFilterChip(
                'Source',
                (resumeSources.find((source: any) => source.id === sourceFilter) || {}).name || sourceFilter,
                () => clearFilterParam('source')
              )}
              {statusFilter && renderFilterChip(
                'Status',
                { uploaded: 'Queued', processing: 'Processing', analyzed: 'Analyzed', failed: 'Failed' }[statusFilter] || statusFilter,
                () => clearFilterParam('status')
              )}
              {isSortActive && renderFilterChip(
                'Sort',
                `${sortByLabel(sortBy)}, ${sortOrder === 'asc' ? 'Asc' : 'Desc'}`,
                clearSort
              )}
            </div>
            <div className="flex items-center gap-3">
              {(skillFilter || jobTitleFilter || minExpFilter || maxExpFilter) && (
                <button
                  onClick={handleClearSearch}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear search
                </button>
              )}
              {activeFilterCount > 0 && (
                <button
                  onClick={handleClearFilters}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  aria-label="Clear filters"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Multi-skill search indicator */}
      {activeSearchTerm && activeSearchTerm.includes(',') && (
        <div className="flex items-center space-x-2 text-sm">
          <span className="text-gray-500">Matching all skills:</span>
          <div className="flex flex-wrap gap-1">
            {activeSearchTerm.split(',').map((skill, i) => skill.trim()).filter(Boolean).map((skill, i) => (
              <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Advanced Search Interface - Hidden for now */}
      <div className="space-y-4 hidden">
        {/* Search Mode Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-medium text-gray-900">Search Candidates</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleSearchModeSwitch(false)}
                className="px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-700"
              >
                Basic Search
              </button>
            </div>
          </div>
          
          {useSemanticSearch && searchIntent && (
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <ChartBarIcon className="w-4 h-4" />
              <span>Intent: {searchIntent.intent_type}</span>
              <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                {Math.round(searchIntent.confidence_score * 100)}% confidence
              </span>
            </div>
          )}
        </div>

        {/* Search Suggestions and Alternatives */}
        {useSemanticSearch && (searchSuggestions.length > 0 || alternativeQueries.length > 0) && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            {searchSuggestions.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center space-x-2 mb-2">
                  <LightBulbIcon className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-800">Suggestions:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {searchSuggestions.map((suggestion, index) => (
                    <span key={index} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                      {suggestion}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {alternativeQueries.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <SparklesIcon className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-800">Try these:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {alternativeQueries.map((query, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSearchInput(query)
                        const newParams = new URLSearchParams(searchParams)
                        newParams.set('search', query)
                        newParams.set('page', '1')
                        setSearchParams(newParams)
                      }}
                      className="text-xs bg-white text-purple-700 border border-purple-300 px-2 py-1 rounded hover:bg-purple-100 transition-colors"
                    >
                      {query}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Search Examples for AI Search */}
        {useSemanticSearch && !activeSearchTerm && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <LightBulbIcon className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-800">Example searches:</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                "React developers with 5+ years experience",
                "Senior engineers with leadership background",
                "Data scientists who know Python and machine learning",
                "Frontend developers with TypeScript skills",
                "DevOps engineers familiar with AWS and Docker",
                "Full-stack developers with startup experience"
              ].map((example, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSearchInput(example)
                    const newParams = new URLSearchParams(searchParams)
                    newParams.set('search', example)
                    newParams.set('page', '1')
                    setSearchParams(newParams)
                  }}
                  className="text-left text-xs text-gray-600 hover:text-purple-600 hover:bg-purple-50 px-2 py-1 rounded transition-colors"
                >
                  "{example}"
                </button>
              ))}
            </div>
          </div>
        )}
      </div>



      {/* Resumes List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Candidates ({totalResumes})
          </h3>

          {totalResumes > 0 && (
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-700">Show:</span>
                <select
                  className="border border-gray-300 rounded-lg px-3 py-1 pr-8 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[60px]"
                  value={pageSize}
                  onChange={(e) => {
                    const newParams = new URLSearchParams(searchParams)
                    newParams.set('page_size', e.target.value)
                    newParams.set('page', '1')
                    setSearchParams(newParams)
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-xs text-gray-700">per page</span>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    const newParams = new URLSearchParams(searchParams)
                    newParams.set('page', String(Math.max(1, currentPage - 1)))
                    setSearchParams(newParams)
                  }}
                  disabled={currentPage === 1}
                  className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-colors"
                  title="Previous page"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>

                <span className="text-xs text-gray-700 px-2">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  onClick={() => {
                    const newParams = new URLSearchParams(searchParams)
                    newParams.set('page', String(currentPage + 1))
                    setSearchParams(newParams)
                  }}
                  disabled={!hasNext}
                  className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-colors"
                  title="Next page"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="p-12 text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600">Loading resumes...</p>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <ExclamationCircleIcon className="w-12 h-12 text-red-400 mx-auto" />
            <p className="mt-4 text-gray-600">Failed to load resumes</p>
          </div>
        ) : resumes.length === 0 ? (
          <div className="p-12 text-center">
            <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto" />
            <p className="mt-4 text-gray-600">No resumes found</p>
            <p className="text-sm text-gray-500">Upload your first resume to get started</p>
          </div>
        ) : (
          <div className="overflow-auto max-h-[65vh]">
            <table className="min-w-[760px] w-full table-fixed divide-y divide-gray-200">
              <colgroup>
                <col style={{ width: '24%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '32%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '9%' }} />
              </colgroup>
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Candidate</th>
                  <th scope="col" className="px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">View</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Contact</th>
                  <th scope="col" className="px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Exp</th>
                  <th scope="col" className="px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th scope="col" className="px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {resumes.map((resume: any) => {
                  const isProcessing = resume.status === 'processing'
                  const stale = isStaleProcessing(resume)
                  const analysisPersonalInfo = resume.analysis && resume.analysis.personal_info
                  const professionalTitle = resume.professional_title || (analysisPersonalInfo && analysisPersonalInfo.professional_title)
                  const isGeneratingFormatted = Boolean(downloadMutation.isPending && downloadMutation.variables && downloadMutation.variables.id === resume.id)
                  const isReprocessingThis = Boolean(reprocessMutation.isPending && reprocessMutation.variables === resume.id)
                  const downloadFormattedLabel = isProcessing
                    ? 'Processing...'
                    : isGeneratingFormatted
                    ? 'Generating formatted resume...'
                    : 'Download Formatted Resume'
                  const reprocessLabel = isProcessing && !stale
                    ? 'Processing...'
                    : stale
                    ? 'Stuck processing — click to retry'
                    : 'Reanalyze'

                  return (
                  <tr key={resume.id} className="align-middle hover:bg-gray-50">
                    {/* Candidate */}
                    <td className="px-3 py-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full font-mono whitespace-nowrap">
                            {resume.candidate_id}
                          </span>
                          <ResumeInfoTooltip content={<ResumeTimingTooltip resume={resume} />}>
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-gray-200 cursor-pointer transition-colors">
                              <InformationCircleIcon className="w-4 h-4 text-gray-700 hover:text-blue-600" />
                            </span>
                          </ResumeInfoTooltip>
                          {resume.blacklist && (
                            <span title="This resume is blacklisted" className="inline-flex">
                              <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
                            </span>
                          )}
                        </div>
                        <h4 className="mt-1 text-sm font-medium text-gray-900 truncate">
                          {resume.candidate_name || resume.filename}
                        </h4>
                        {professionalTitle ? (
                          <div className="text-xs text-blue-600 font-medium truncate">
                            {professionalTitle}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400">No professional title available</div>
                        )}
                        <DuplicateBadge resume={resume} />
                      </div>
                    </td>

                    {/* View */}
                    <td className="px-2 py-2 text-center">
                      <button
                        className={`inline-flex p-1.5 rounded-lg transition-all ${
                          isProcessing
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                        onClick={() => navigate(`/resumes/${resume.id}`)}
                        disabled={isProcessing}
                      >
                        <EyeIcon className="w-5 h-5" />
                      </button>
                    </td>

                    {/* Contact */}
                    <td className="px-3 py-2">
                      {(resume.candidate_phone || resume.candidate_email) ? (
                        <div className="text-xs text-gray-600 space-y-0.5">
                          {resume.candidate_phone && <div>{formatPhoneNumber(resume.candidate_phone)}</div>}
                          {resume.candidate_email && (
                            <div className="truncate" title={resume.candidate_email}>{resume.candidate_email}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    {/* Experience */}
                    <td className="px-2 py-2 text-center">
                      {(resume.years_experience || resume.total_years_experience) ? (
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {resume.years_experience || resume.total_years_experience}
                          </div>
                          <div className="text-[10px] text-gray-500">Years Exp</div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-2 py-2 text-center">
                      <div className="flex justify-center">
                        <StatusBadge status={resume.status} />
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-2 py-2 text-center">
                      <ResumeRowActionsMenu
                        canDownloadOriginal={canDownloadOriginal}
                        canDownloadFormatted={canDownloadFormatted}
                        canReprocess={canReprocess}
                        canDelete={isAdmin}
                        downloadOriginalDisabled={isProcessing || downloadOriginalMutation.isPending}
                        downloadFormattedDisabled={isProcessing || resume.status !== 'analyzed' || isGeneratingFormatted}
                        downloadFormattedLabel={downloadFormattedLabel}
                        isGeneratingFormatted={isGeneratingFormatted}
                        reprocessDisabled={(isProcessing && !stale) || isReprocessingThis}
                        reprocessLabel={reprocessLabel}
                        isReprocessing={isReprocessingThis}
                        deleteDisabled={isProcessing || showLoadingPopup || deleteMutation.isPending}
                        onDownloadOriginal={() => downloadOriginalMutation.mutate({ id: resume.id, filename: resume.filename })}
                        onDownloadFormatted={() => handleDownloadResume(resume)}
                        onReprocess={() => {
                          toast.success('Resume reprocessing started')
                          reprocessMutation.mutate(resume.id)
                        }}
                        onDelete={() => handleOpenDeleteModal(resume.id)}
                      />
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bottom Pagination */}
      {totalResumes > 0 && (
        <div className="flex justify-center items-center space-x-4 bg-white p-4 rounded-lg shadow">
          <button
            onClick={() => {
              const p = new URLSearchParams(searchParams)
              p.set('page', String(Math.max(1, currentPage - 1)))
              setSearchParams(p)
            }}
            disabled={currentPage === 1}
            className="flex items-center space-x-2 px-4 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            <span>Previous</span>
          </button>

          <div className="flex items-center space-x-2">
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let startPage = Math.max(1, currentPage - 2)
              const endPage = Math.min(totalPages, startPage + 4)

              if (endPage - startPage < 4) {
                startPage = Math.max(1, endPage - 4)
              }

              const pageNum = startPage + i
              if (pageNum > totalPages) return null

              return (
                <button
                  key={pageNum}
                  onClick={() => {
                    const p = new URLSearchParams(searchParams)
                    p.set('page', String(pageNum))
                    setSearchParams(p)
                  }}
                  className={`px-3 py-2 text-xs rounded-lg transition-colors ${
                    currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              )
            })}
          </div>

          <button
            onClick={() => {
              const p = new URLSearchParams(searchParams)
              p.set('page', String(currentPage + 1))
              setSearchParams(p)
            }}
            disabled={!hasNext}
            className="flex items-center space-x-2 px-4 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span>Next</span>
            <ChevronRightIcon className="w-4 h-4" />
          </button>

          <div className="text-xs text-gray-600 ml-4">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalResumes)} of {totalResumes} results
          </div>
        </div>
      )}

      {/* Upload Modal */}
      <UploadModal 
        isOpen={isUploadModalOpen} 
        onClose={() => setIsUploadModalOpen(false)} 
      />
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Resume"
        isLoading={deleteMutation.isPending}
      >
        Are you sure you want to delete this resume? This action cannot be undone.
      </ConfirmationModal>
    </div>
  )
}

export default ResumesPage